import { z } from 'zod';

/**
 * Critères de recherche, portés par l'URL.
 *
 * L'URL est la source de vérité : une recherche est partageable, indexable par
 * Google, et le bouton « retour » du navigateur fonctionne naturellement.
 * Les noms de paramètres sont en français, ils apparaissent dans la barre
 * d'adresse.
 */

const csv = z
  .string()
  .optional()
  .transform((value) => (value ? value.split(',').filter(Boolean) : []));

export const searchCriteriaSchema = z.object({
  lieu: z.string().min(1).default('abidjan'),
  depart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  heureDepart: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('10:00'),
  retour: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  heureRetour: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .default('10:00'),

  categories: csv,
  marques: csv,
  equipements: csv,
  boite: z.enum(['manual', 'automatic']).optional(),
  carburant: z.enum(['petrol', 'diesel', 'hybrid', 'electric']).optional(),
  places: z.coerce.number().int().min(1).optional(),
  prixMin: z.coerce.number().int().min(0).optional(),
  prixMax: z.coerce.number().int().min(0).optional(),
  chauffeur: z.coerce.boolean().optional(),
  livraison: z.coerce.boolean().optional(),

  tri: z.enum(['price_asc', 'price_desc', 'rating_desc', 'popular']).default('price_asc'),
  page: z.coerce.number().int().min(1).default(1),
});

export type SearchCriteria = z.infer<typeof searchCriteriaSchema>;

export const PAGE_SIZE = 12;

export function parseSearchParams(params: URLSearchParams): SearchCriteria {
  const raw = Object.fromEntries(params.entries());
  const result = searchCriteriaSchema.safeParse(raw);
  // Une URL bricolée à la main ne doit pas casser la page : on retombe sur les
  // valeurs par défaut.
  return result.success ? result.data : searchCriteriaSchema.parse({});
}

export function criteriaToSearchParams(criteria: Partial<SearchCriteria>): URLSearchParams {
  const params = new URLSearchParams();
  const push = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '' || value === false) return;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
      return;
    }
    params.set(key, String(value));
  };

  push('lieu', criteria.lieu);
  push('depart', criteria.depart);
  push('heureDepart', criteria.heureDepart);
  push('retour', criteria.retour);
  push('heureRetour', criteria.heureRetour);
  push('categories', criteria.categories);
  push('marques', criteria.marques);
  push('equipements', criteria.equipements);
  push('boite', criteria.boite);
  push('carburant', criteria.carburant);
  push('places', criteria.places);
  push('prixMin', criteria.prixMin);
  push('prixMax', criteria.prixMax);
  push('chauffeur', criteria.chauffeur);
  push('livraison', criteria.livraison);
  if (criteria.tri && criteria.tri !== 'price_asc') push('tri', criteria.tri);
  if (criteria.page && criteria.page > 1) push('page', criteria.page);

  return params;
}

/** Traduit les critères d'URL en paramètres de la fonction SQL search_vehicles. */
export function criteriaToRpcParams(criteria: SearchCriteria): Record<string, unknown> {
  const start =
    criteria.depart && criteria.retour
      ? new Date(`${criteria.depart}T${criteria.heureDepart}`).toISOString()
      : null;
  const end =
    criteria.depart && criteria.retour
      ? new Date(`${criteria.retour}T${criteria.heureRetour}`).toISOString()
      : null;

  return {
    city: criteria.lieu,
    start,
    end,
    categories: criteria.categories,
    brands: criteria.marques,
    features: criteria.equipements,
    transmission: criteria.boite ?? null,
    fuel: criteria.carburant ?? null,
    min_seats: criteria.places ?? null,
    min_price: criteria.prixMin ?? null,
    max_price: criteria.prixMax ?? null,
    with_driver: criteria.chauffeur ?? false,
    delivery: criteria.livraison ?? false,
    sort: criteria.tri,
    limit: PAGE_SIZE,
    offset: (criteria.page - 1) * PAGE_SIZE,
  };
}

/** Nombre de filtres actifs, pour l'étiquette du bouton « Filtres » sur mobile. */
export function countActiveFilters(criteria: SearchCriteria): number {
  return (
    criteria.categories.length +
    criteria.marques.length +
    criteria.equipements.length +
    (criteria.boite ? 1 : 0) +
    (criteria.carburant ? 1 : 0) +
    (criteria.places ? 1 : 0) +
    (criteria.prixMin ? 1 : 0) +
    (criteria.prixMax ? 1 : 0) +
    (criteria.chauffeur ? 1 : 0) +
    (criteria.livraison ? 1 : 0)
  );
}
